/**
 * ConfigurationStep — Create Test Wizard (combined Scenario Config + General Config)
 * Step 6F Green Phase
 */

import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Separator } from "../../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Pencil } from "lucide-react";
import { apiClient } from "../../services/apiClient";
import { showToast } from "../../lib/toast";
import type { WizardStepProps } from "./VersionSelectionStep";

export function ConfigurationStep({ wizardData, onNext, onBack }: WizardStepProps) {
  const scenario = Number(wizardData?.scenario) || 1;

  // -- Scenario-specific state
  const [comparisonDisclosure, setComparisonDisclosure] = useState(true);

  // -- General config state
  const [backNavigation, setBackNavigation] = useState<boolean>(
    (wizardData?.back_navigation_enabled as boolean) ?? false
  );
  const [minOptionPointsThreshold, setMinOptionPointsThreshold] = useState<number>(
    (wizardData?.min_option_points_threshold as number) ?? 20
  );

  // -- Invitation email state.
  // Tracked as `string | undefined`: undefined means "admin never opened
  // the editor — send the canonical default template". A string (even
  // empty) means "admin customized, use what they saved". This makes the
  // back-end's use_custom gate triggered deliberately, not by accident.
  const [invitationSubject, setInvitationSubject] = useState<string | undefined>(
    wizardData?.invitation_subject as string | undefined
  );
  const [invitationBody, setInvitationBody] = useState<string | undefined>(
    wizardData?.invitation_body as string | undefined
  );
  const [defaultTemplate, setDefaultTemplate] = useState<{ subject: string; body: string } | null>(null);

  // Dialog state — draft fields only flow into invitationSubject/Body on Save.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");

  useEffect(() => {
    apiClient
      .get<{ subject: string; body: string }>("/api/invitation-template/default")
      .then(setDefaultTemplate)
      .catch(() => {
        // Surface the failure so a backend regression is visible to the
        // admin (and the team) instead of silently leaving the editor
        // empty. The wizard still works — admin can type their own
        // subject/body — but they know the default is unavailable.
        showToast({
          variant: "warning",
          title: "Default invitation template unavailable",
          description: "Type your invitation message manually, or refresh to try again.",
        });
      });
  }, []);

  const isCustomized = invitationSubject !== undefined || invitationBody !== undefined;

  const handleOpenEditor = () => {
    // Seed draft from saved edits if any; otherwise from the canonical default.
    setDraftSubject(invitationSubject ?? defaultTemplate?.subject ?? "");
    setDraftBody(invitationBody ?? defaultTemplate?.body ?? "");
    setDialogOpen(true);
  };

  const handleResetDraft = () => {
    if (defaultTemplate) {
      setDraftSubject(defaultTemplate.subject);
      setDraftBody(defaultTemplate.body);
    }
  };

  const handleSaveDraft = () => {
    setInvitationSubject(draftSubject);
    setInvitationBody(draftBody);
    setDialogOpen(false);
  };

  // No scenario-specific data fetching needed at creation time
  // Scenario 5 baseline selection is done post-creation by the Client Manager

  const handleNext = () => {
    onNext?.({
      // Scenario-specific
      comparison_disclosure: comparisonDisclosure,
      // General config
      back_navigation_enabled: backNavigation,
      min_option_points_threshold: minOptionPointsThreshold,
      // Invitation email
      invitation_subject: invitationSubject,
      invitation_body: invitationBody,
    });
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Configuration</h2>
      <p className="text-sm text-brand-steel mb-6">Configure scenario-specific and general test settings.</p>

      <Card className="border-brand-steel/50 shadow-sm mb-6">
        <CardContent className="py-6 px-8">
          {/* ---- Scenario-Specific Section ---- */}
          <div className="space-y-4">
            <p className="text-xs font-semibold text-brand-steel uppercase tracking-wider">Scenario Settings</p>

            {scenario === 1 && (
              <p className="text-sm">
                No additional configuration required for Self Reflection. The active version will be used for
                self-registration.
              </p>
            )}

            {scenario === 2 && (
              <p className="text-sm">
                No additional configuration required for Pair Comparative. Baseline settings are configured in the Details step.
              </p>
            )}

            {scenario === 3 && (
              <div>
                <p className="text-sm text-brand-steel mb-3">
                  Comparison disclosure messaging: Participants will be informed that their results may be compared with
                  peers.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    id="comparison-disclosure"
                    type="checkbox"
                    checked={comparisonDisclosure}
                    onChange={(e) => setComparisonDisclosure(e.target.checked)}
                    className="rounded border-brand-steel/50"
                    aria-label="Enable comparison disclosure"
                  />
                  <label htmlFor="comparison-disclosure" className="text-sm text-gray-700">
                    Enable comparison disclosure messaging
                  </label>
                </div>
              </div>
            )}

            {scenario === 4 && (
              <div className="p-4 bg-brand-highlight/40 border border-brand-warning/50 rounded-lg">
                <p className="text-sm font-semibold text-amber-800 mb-1">Role Time Prioritization Required</p>
                <p className="text-sm text-amber-700">
                  Results will be locked until the Client Manager provides the Time Prioritization allocation.
                </p>
              </div>
            )}

            {scenario === 5 && (
              <div className="p-4 bg-purple-50/50 border border-purple-400 rounded-lg">
                <p className="text-sm font-semibold text-purple-800 mb-1">Baseline Required</p>
                <p className="text-sm text-purple-700">
                  Results will be locked until the Client Manager selects a baseline profile and defines the Succession Time Prioritization allocation.
                </p>
              </div>
            )}
          </div>

          <Separator className="my-6" />

          {/* ---- General Config Section ---- */}
          <div className="space-y-4">
            <p className="text-xs font-semibold text-brand-steel uppercase tracking-wider">General Settings</p>

            <div>
              <div className="flex items-center gap-3">
                <Switch
                  id="back-navigation"
                  checked={backNavigation}
                  onCheckedChange={setBackNavigation}
                  aria-label="Enable Back Navigation"
                />
                <label htmlFor="back-navigation" className="text-sm text-gray-700 cursor-pointer">
                  Allow candidates to navigate back to previous questions
                </label>
              </div>
              <p className="text-xs text-brand-steel/70 mt-1">
                One step at a time: candidates can reopen their immediately previous answer to revise it, then
                continue. Off by default.
              </p>
            </div>

            <div>
              <label htmlFor="min-option-threshold" className="block text-sm font-medium text-gray-700 mb-1">
                Min Option Points Threshold
              </label>
              <input
                id="min-option-threshold"
                type="number"
                value={minOptionPointsThreshold}
                onChange={(e) => setMinOptionPointsThreshold(Math.max(0, Math.min(100, Number(e.target.value))))}
                min={0}
                max={100}
                className="h-9 border border-brand-steel/50 bg-white rounded-lg px-3 text-sm text-gray-900 w-32 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
              />
              <p className="text-xs text-brand-steel/70 mt-1">
                Each questionnaire answer must place at least this many points on one option. Set 0 to disable.
              </p>
            </div>
          </div>

          <Separator className="my-6" />

          {/* ---- Invitation Email Section ---- */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-brand-steel uppercase tracking-wider">Invitation Email</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleOpenEditor}
                disabled={!defaultTemplate}
                className="inline-flex items-center gap-2"
              >
                <Pencil size={16} aria-hidden="true" />
                {isCustomized ? "Edit" : "Customize"}
              </Button>
            </div>
            <div className="rounded-lg border border-brand-steel/50 bg-brand-panel/40 p-3">
              {isCustomized ? (
                <>
                  <p className="text-xs text-brand-steel/70 mb-1">Custom message will be sent.</p>
                  <p className="text-sm font-medium text-gray-900 truncate" title={invitationSubject}>
                    {invitationSubject?.trim() || "(no subject)"}
                  </p>
                  <p className="text-xs text-brand-steel mt-2 line-clamp-2 whitespace-pre-wrap">
                    {invitationBody?.trim() || "(no body)"}
                  </p>
                </>
              ) : (
                <p className="text-sm text-brand-steel">
                  The default invitation message will be sent to each participant when the test is launched.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invitation editor dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white rounded-xl shadow-2xl max-w-2xl border-brand-steel/50">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-gray-900">Customize Invitation Email</DialogTitle>
            <DialogDescription className="text-sm text-brand-steel">
              Sent to each participant when the test is launched. Placeholders like{" "}
              <code className="font-mono text-xs bg-brand-panel px-1 rounded">{"{candidate_name}"}</code>,{" "}
              <code className="font-mono text-xs bg-brand-panel px-1 rounded">{"{candidate_email}"}</code>,{" "}
              <code className="font-mono text-xs bg-brand-panel px-1 rounded">{"{candidate_password}"}</code>,{" "}
              <code className="font-mono text-xs bg-brand-panel px-1 rounded">{"{assessment_link}"}</code>, and{" "}
              <code className="font-mono text-xs bg-brand-panel px-1 rounded">{"{admin_name}"}</code> are filled in per recipient — remove any you don't want.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveDraft();
            }}
            className="space-y-4"
          >
            <div>
              <label htmlFor="invitation-subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <input
                id="invitation-subject"
                type="text"
                value={draftSubject}
                onChange={(e) => setDraftSubject(e.target.value)}
                className="w-full h-9 rounded-lg border border-brand-steel/50 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
              />
            </div>

            <div>
              <label htmlFor="invitation-body" className="block text-sm font-medium text-gray-700 mb-1">
                Body
              </label>
              <textarea
                id="invitation-body"
                rows={14}
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                className="w-full border border-brand-steel/50 bg-white rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson resize-none"
              />
            </div>

            <DialogFooter className="flex justify-between gap-3 pt-2 sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={handleResetDraft}
                disabled={!defaultTemplate}
              >
                Reset to default
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="flex justify-between">
        <div>
          {onBack && (
            <Button type="button" variant="outline" onClick={() => onBack?.()} className="min-w-25 border-brand-steel/40">
              Back
            </Button>
          )}
        </div>
        <Button
          type="button"
          onClick={handleNext}
          className="min-w-25 bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-6 py-2"
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export default ConfigurationStep;
